'use client';

import React from 'react';
import type { SubmenuTriggerProps as AriaSubmenuTriggerProps } from 'react-aria-components';
import { SubmenuTrigger as AriaSubmenuTrigger } from 'react-aria-components';
import type { PopoverProps } from '../popover';
import { Popover } from '../popover';

const COMMAND_MENU_SUBMENU_DISPLAY_NAME = 'RomaCommandMenuSubmenu';
const COMMAND_MENU_SUBMENU_POPOVER_DISPLAY_NAME = 'RomaCommandMenuSubmenuPopover';

// React Aria doesn't provide a way to disable hover/cursor-triggered opening of submenus, so we use a very long delay as a workaround when `clickOnlyTrigger` is true.
// This allows the submenu to still open in response to clicks, while effectively disabling hover/cursor-triggered opening.
const CLICK_ONLY_TRIGGER_DELAY = 60_000 * 60 * 24 * 365; // If you hover over the trigger for a year, the submenu will open :)

export type CommandMenuSubmenuPopoverProps = PopoverProps;

export type CommandMenuSubmenuProps = Omit<AriaSubmenuTriggerProps, 'children'> & {
    children: React.ReactNode;
    clickOnlyTrigger?: boolean;
};

/**
 * Thin wrapper over React Aria's `SubmenuTrigger`.
 * The first child must be the trigger row and the second child must be the submenu popover.
 */
export const CommandMenuSubmenu = ({
    children,
    clickOnlyTrigger,
    delay,
    ...props
}: CommandMenuSubmenuProps): React.JSX.Element => {
    const submenuChildren = React.Children.toArray(children);
    const triggerChild = submenuChildren[0];

    if (clickOnlyTrigger && React.isValidElement(triggerChild)) {
        submenuChildren[0] = React.cloneElement(triggerChild, {
            'data-click-only-trigger': true,
        });
    }

    return (
        <AriaSubmenuTrigger {...(props as any)} delay={clickOnlyTrigger ? CLICK_ONLY_TRIGGER_DELAY : delay}>
            {submenuChildren as any}
        </AriaSubmenuTrigger>
    );
};

/**
 * Thin wrapper over Roma `Popover` for command menu submenus.
 * Defaults the submenu offset while still allowing callers to override it.
 */
export const CommandMenuSubmenuPopover = ({
    offset,
    crossOffset,
    ...props
}: CommandMenuSubmenuPopoverProps): React.JSX.Element => (
    <Popover {...props} offset={offset ?? 2} crossOffset={crossOffset ?? -4} />
);

CommandMenuSubmenu.displayName = COMMAND_MENU_SUBMENU_DISPLAY_NAME;
CommandMenuSubmenuPopover.displayName = COMMAND_MENU_SUBMENU_POPOVER_DISPLAY_NAME;
